import { defineStore } from 'pinia';
import { filter, get, isEmpty, map, mapValues, union, uniq } from 'lodash-es';
import Division from '@/entities/Division.js';
import TaskEventType from '@/entities/TaskEventType.js';
import HyundaiSegments from '@/entities/HyundaiSegments.js';
import LeadVehicleState from '@/entities/LeadVehicleState.js';
import LeadVehicleTerm from '@/entities/LeadVehicleTerm.js';
import CampaignType from '@/entities/CampaignType.js';
import CaslConsentState from '@/entities/CaslConsentState.js';
import CreatedMethod from '@/entities/CreatedMethod.js';
import { useClientCardStore } from '@/store/modules/clientCard/store.js';
import { useGlobalStore } from '@/store/store.ts';
import { useDashboardContextStore } from '@/store/modules/dashboardContext/store.ts';
import { useContextStore } from '@/store/modules/context/store.ts';
import { useAccountStore } from '@/store/modules/account/store.js';
import ActivixDate from '@/value-objects/ActivixDate.js';
import SupplierId from '@/entities/enums/SupplierId.js';
import DashboardType from '@/entities/DashboardType.js';
import i18n from '@/plugins/vue-i18n.js';
import useFeature from '@/plugins/optimizely.js';
import { formater } from '@/globals/LeadFormatter.js';
import { getRegionalDisplayName } from '@/utils/translations.ts';
import Civility from '@/entities/Civility.js';
import LeadVehicleCondition from '@/entities/LeadVehicleCondition.js';
import LeadVehicleFuel from '@/entities/LeadVehicleFuel.js';
import { getIconMarkup } from '@/plugins/svg-icons.ts';
import Moment from '@/value-objects/Moment.js';
import ProgressState from '@/entities/ProgressState.js';
import ClientCardSection from '@/entities/ClientCardSection.js';
import {
    fallbackAccountVehicleModels,
    fallbackAuthUser,
    fallbackContextAccount,
} from '@/store/modules/dashboardColumns/fallbackObjects.ts';
import { useUserStore } from '@/store/modules/user/store.js';
import Province from '@/entities/Province.js';
import Country from '@/entities/Country.js';
import Locale from '@/entities/Locale.js';

const asLocale = (
    date: string,
    key: string,
    format: string = '',
    timezone: string = '',
    defaultNow: boolean = false
) => {
    return Moment.asLocale(date, key, { format, timezone, defaultNow });
};

const noopAsync = () => Promise.resolve();

export interface DashboardColumnsState {
    activeUsers: Array<any>;
    activeUsersAndSupplierUsers: Array<any>;
    customerProfileDashboardLayoutEnabled: boolean;
    directorUsers: Array<any>;
    notServiceUsers: Array<any>;
}

interface ColumnDefinition {
    name: string;
    title: string | (() => string);
    isValid?: () => boolean;
    formater?: (...args: any[]) => any;
    style?: Record<string, any>;
    truncating?: boolean;
    baseClass?: string;
    class?: string;
    component?: string;
    disableSorting?: boolean;
    fixed?: boolean;
    mandatory?: boolean;
    sortTitle?: string;
}

interface DashboardConfigs {
    columns: DashboardColumnConfig[];
    columnOrder: string[];
    columnVisibilities: Record<string, string>;
    filters: Record<string, any>;
}

interface DashboardColumnConfig {
    name: string;
    visible: boolean;
}

const MANDATORY_COLUMN_ORDER = ['bulk_select', 'lead_navigation', 'fullname', 'contact_method'];

const addEligibleLeadNavigationColumn = (
    configuredColumns: DashboardColumnConfig[],
    columns: Record<string, ColumnDefinition>
): DashboardColumnConfig[] => {
    const columnsWithLeadNavigation = [...configuredColumns];
    const configuredColumnNames = new Set(configuredColumns.map((column) => column.name));
    const leadNavigationColumn = columns.lead_navigation;
    const leadNavigationIsValid = !leadNavigationColumn?.isValid || leadNavigationColumn.isValid();

    if (
        leadNavigationColumn?.mandatory &&
        leadNavigationIsValid &&
        !configuredColumnNames.has('lead_navigation')
    ) {
        columnsWithLeadNavigation.push({ name: 'lead_navigation', visible: true });
    }

    return columnsWithLeadNavigation;
};

const normalizeMandatoryColumnOrder = (
    columnOrder: string[],
    configuredColumnNames: Set<string>
): string[] => {
    const configuredMandatoryColumns = MANDATORY_COLUMN_ORDER.filter((columnName) =>
        configuredColumnNames.has(columnName)
    );
    const configuredMandatoryColumnNames = new Set(configuredMandatoryColumns);
    const nonMandatoryColumnOrder = columnOrder.filter(
        (columnName) => !configuredMandatoryColumnNames.has(columnName)
    );

    return [...configuredMandatoryColumns, ...nonMandatoryColumnOrder];
};

/**
 * Compute the dashboard columns.
 * This algorithm avoids unnecessary mapping/filtering for performance.
 * Exported as a standalone function so that it is easier to test.
 */
export function computeDashboardColumns(
    dashboardConfigs: DashboardConfigs,
    columns: Record<string, ColumnDefinition>
): Array<ColumnDefinition> {
    // TODO: it would be nice to prevent this from being called multiple times on page load.
    // But this is hard because each time a dependency of any column is re-evaluated, this will be called.
    const columnsFromConfig = addEligibleLeadNavigationColumn(dashboardConfigs.columns, columns);
    const configuredColumnNames = new Set(columnsFromConfig.map((column) => column.name));
    const columnOrder = normalizeMandatoryColumnOrder(
        dashboardConfigs.columnOrder || [],
        configuredColumnNames
    );
    const result = [];

    // Create an efficient lookup map for columns by name
    const columnMap = {} as Record<string, any>;
    for (let i = 0; i < columnsFromConfig.length; i++) {
        columnMap[columnsFromConfig[i].name] = columnsFromConfig[i];
    }

    // Create position map for lookups during sorting
    const columnPositionMap = {} as Record<string, number>;

    // Process column order
    if (columnOrder.length) {
        // Add missing columns to columnOrder in one pass
        const existingColumns = new Set(columnOrder);
        for (let i = 0; i < columnsFromConfig.length; i++) {
            if (!existingColumns.has(columnsFromConfig[i].name)) {
                columnOrder.push(columnsFromConfig[i].name);
            }
        }

        // Create position map in one pass
        for (let i = 0; i < columnOrder.length; i++) {
            columnPositionMap[columnOrder[i]] = i;
        }
    }

    // Process columns
    for (let i = 0; i < columnsFromConfig.length; i++) {
        const dashboardColumn = columnsFromConfig[i];
        const column = columns[dashboardColumn.name];

        if (!column) {
            continue;
        }

        const visibility = dashboardConfigs.columnVisibilities[column.name];
        const title = typeof column.title === 'function' ? column.title() : column.title;

        // NOTE that we have 3 states of "validity":
        // - implicitly valid (no isValid function)
        // - explicitly valid (isValid function returns true)
        // - explicitly invalid (isValid function returns false)
        const hasValidityFunction = typeof column.isValid !== 'undefined';
        const explicitValidity = hasValidityFunction && column.isValid && column.isValid();
        const isValid = hasValidityFunction ? explicitValidity : true;

        if (!isValid) {
            // Filter out invalid columns
            continue;
        }

        result.push({
            ...column,
            title,
            visible:
                visibility === 'visible' || (visibility !== 'hidden' && dashboardColumn.visible),
            class: [
                column.baseClass || '',
                column.class || '',
                `column-${dashboardColumn.name
                    .replaceAll(/([a-z])([A-Z])/g, '$1-$2')
                    .replaceAll('_', '-')
                    .toLowerCase()}`,
            ]
                .join(' ')
                .trim(),
            sortTitle: column.sortTitle || title || '',
        });
    }

    // Sort columns
    if (columnOrder.length) {
        result.sort((a, b) => columnPositionMap[a.name] - columnPositionMap[b.name]);
    }
    return result;
}

export const useDashboardColumnsStore = defineStore('dashboardColumns', {
    state: (): DashboardColumnsState => ({
        activeUsers: [],
        activeUsersAndSupplierUsers: [],
        customerProfileDashboardLayoutEnabled: false,
        directorUsers: [],
        notServiceUsers: [],
    }),
    getters: {
        // Proxy Pinia store state as getters
        leadBanks: () => useClientCardStore().leadBanks,
        authUser: () => (useUserStore().authUser as any) ?? fallbackAuthUser,
        configs: () => useGlobalStore().configs,
        groupDisplay: () => useGlobalStore().groupDisplay,
        session: () => useUserStore().session ?? {},
        accountVehicleModels: () => {
            const accountStore = useAccountStore();
            return accountStore?.accountVehicleModels ?? fallbackAccountVehicleModels;
        },
        accountVehicleModelsLoaded: () => {
            const accountStore = useAccountStore();
            return accountStore?.accountVehicleModelsLoaded ?? false;
        },
        fetchAccountVehicleModels: () => {
            const accountStore = useAccountStore();
            return accountStore?.fetchAccountVehicleModels ?? noopAsync;
        },
        dashboardConfigs: () => useDashboardContextStore().configs,
        startDate: () => useDashboardContextStore().startDate,
        endDate: () => useDashboardContextStore().endDate,
        dashboardType: () => useDashboardContextStore().dashboardType,
        contextAccount: (() => {
            const accountStore = useAccountStore();
            return accountStore?.contextAccount ?? fallbackContextAccount;
        }) as any,
        contextGroup: () => useContextStore().group as any,

        attributionColumnNames() {
            return [
                'lead_type:name',
                'division:name',
                'source',
                'form',
                'wanted_vehicles:make',
                'wanted_vehicles:model',
                'wanted_vehicles:year',
                'wanted_vehicles:stock',
                'wanted_vehicles:version',
                'wanted_vehicles:vin',
                'exchange_vehicles:make',
                'exchange_vehicles:model',
                'exchange_vehicles:year',
                'exchange_vehicles:stock',
                'exchange_vehicles:version',
                'exchange_vehicles:vin',
            ];
        },

        calculatorValueVisible() {
            const user = this.authUser as any;
            return (
                user.isAdmin() ||
                user.isDirector() ||
                user.isBdcDirector() ||
                user.isCommercialDirector() ||
                user.calculator_value_visible
            );
        },
        parsedStartDate() {
            return new ActivixDate(this.startDate).startOfDay();
        },
        parsedEndDate() {
            return new ActivixDate(this.endDate).endOfDay();
        },
        legacyClientColumnTitle() {
            switch (this.dashboardType) {
                case DashboardType.WEB_ORDER:
                    return i18n.t('general.webOrder', 0);

                case DashboardType.WALK_IN:
                    return i18n.t('general.walkIn');

                case DashboardType.PHONE_UP:
                    return i18n.t('general.phoneUp');

                case DashboardType.ALL:
                case DashboardType.RENEWAL:
                case DashboardType.COMMERCIAL:
                case DashboardType.LOYALTY:
                case DashboardType.SERVICE:
                case DashboardType.SALE:
                case DashboardType.EVENT:
                    return i18n.t('general.client', 0);

                default:
                    return i18n.t('general.lead', 0);
            }
        },
        clientColumnTitle() {
            return this.customerProfileDashboardLayoutEnabled
                ? i18n.t('general.client', 0)
                : this.legacyClientColumnTitle;
        },
        leadNavigationColumnTitle() {
            switch (this.dashboardType) {
                case DashboardType.WEB_ORDER:
                    return i18n.t('general.webOrder', 1);

                case DashboardType.WALK_IN:
                    return i18n.t('general.walkIn', 1);

                case DashboardType.PHONE_UP:
                    return i18n.t('general.phoneUp', 1);

                default:
                    return i18n.t('general.lead', 1);
            }
        },
        dateColumnTooltip() {
            switch (this.dashboardType) {
                case DashboardType.RENEWAL:
                    return i18n.t('date.createdDate');

                case DashboardType.SERVICE:
                    return `
                        ${i18n.t('date.presentedDate')}<br>
                        ${i18n.t('date.appointmentDate')}<br>
                        ${i18n.t('date.createdDate')}
                    `;

                case DashboardType.ALL: {
                    let webOrderType = '';

                    if (this.authUser.isAdmin() || this.authUser.hasWebOrder()) {
                        webOrderType = `
                            <span class="font-semibold">${i18n.t('dashboards.webOrder')}</span> :
                            ${i18n.t('date.createdDate')}<br>
                        `;
                    }

                    return `
                        <div class="text-left">
                            ${webOrderType}
                            <span class="font-semibold">${i18n.t('dashboards.webLead')}</span> :
                            ${i18n.t('date.createdDate')}<br>
                            <span class="font-semibold">${i18n.t('dashboards.phoneUp')}</span> :
                            ${i18n.t('date.callDate')}<br>
                            <span class="font-semibold">${i18n.t('dashboards.walkIn')}</span> :
                            ${i18n.t('date.presentedDate')}<br>
                            <span class="font-semibold">${i18n.t('dashboards.loyalty')}</span> :
                            ${i18n.t('date.createdDate')}<br>
                            <span class="font-semibold">${i18n.t('dashboards.renewal')}</span> :
                            ${i18n.t('date.endContractDate')}<br>
                        </div>
                    `;
                }

                default:
                    return '';
            }
        },
        accountHasNioText() {
            return (
                this.contextAccount.hasSmsCapability() ||
                this.contextAccount.children.some((child: any) => child.hasSmsCapability())
            );
        },

        accountHasMessenger() {
            return (
                this.contextAccount.hasMessenger() ||
                this.contextAccount.children.some((child: any) => child.hasMessenger())
            );
        },

        accountHasVideoConference() {
            return (
                this.contextAccount.video_conference ||
                this.contextAccount.children.some((child: any) => child.video_conference)
            );
        },

        suppliers() {
            const suppliers: Array<{ text: string; value: any }> = [];

            Object.keys(SupplierId.entries).forEach((key) => {
                const typedKey = key as keyof typeof SupplierId.entries;
                suppliers.push({
                    text: i18n.t(`suppliers.${[key]}`),
                    value: SupplierId.entries[typedKey],
                });
            });

            return suppliers;
        },

        accountOptions() {
            if (this.contextGroup.id) {
                return this.contextGroup.accounts.map((account: any) => {
                    return {
                        value: account.id,
                        text: account.name,
                    };
                });
            }
            return (this.contextAccount.children || []).map((childAccount: any) => {
                return {
                    value: childAccount.id.toString(),
                    text: childAccount.name,
                };
            });
        },

        sourceOptions() {
            return this.contextAccount.sources
                .map((source: any) => {
                    if (this.contextAccount.display_approved_sources && source.approved_source) {
                        return {
                            value: source.approved_source.name.trim(),
                            text: (
                                getRegionalDisplayName(source.approved_source, i18n.getLocale()) ??
                                source.approved_source.name
                            ).trim(),
                        };
                    }
                    return {
                        value: source.name.trim(),
                        text: source.name.trim(),
                    };
                })
                .sort((a: any, b: any) => a.text.trim().localeCompare(b.text.trim()));
        },

        phoneAppointmentOptions() {
            return [
                {
                    value: 'yes',
                    text: i18n.t('general.yes'),
                },
                {
                    value: 'no',
                    text: i18n.t('general.no'),
                },
                'canceled',
                'confirmed',
                'noDate',
                'notConfirmed',
                'toReach',
                'uncertain',
                'completed',
                'notCompleted',
            ];
        },

        appointmentOptions() {
            return [
                {
                    value: 'yes',
                    text: i18n.t('general.yes'),
                },
                {
                    value: 'no',
                    text: i18n.t('general.no'),
                },
                {
                    value: 'noTime',
                    condition: this.dashboardType == 'event',
                },
                'canceled',
                'confirmed',
                'noDate',
                'noShow',
                'notConfirmed',
                'toReach',
                'uncertain',
                'completed',
                'notCompleted',
                'tomorrow',
            ];
        },

        caslConsentLimitDateOptions() {
            const values = [];

            // 10 days
            values.push({
                value: '<= 10',
                text: i18n.t('consent.durations.lessThanTenDays'),
            });

            // 11 - 30 days
            values.push({
                value: ['>=11', '<= 30'],
                text: i18n.t('consent.durations.elevenToThirtyDays'),
            });

            // 1 - 3 months
            values.push({
                value: ['>=31', '<=120'],
                text: i18n.t('consent.durations.oneToThreeMonths'),
            });

            // 4 months - 11 months
            values.push({
                value: ['>=121', '<=364'],
                text: i18n.t('consent.durations.fourToElevenMonths'),
            });

            // 12 months - 24 months
            values.push({
                value: ['>=365', '<=731'],
                text: i18n.t('consent.durations.twelveToTwentyFourMonths'),
            });

            return values;
        },

        civilityOptions() {
            return Civility.values.map((civility) => {
                return {
                    value: civility,
                    text: Civility.getTranslation(civility),
                };
            });
        },

        conditionOptions() {
            return LeadVehicleCondition.values.map((condition) => {
                return {
                    value: condition,
                    text: LeadVehicleCondition.getTranslation(condition),
                };
            });
        },

        fuelOptions() {
            return LeadVehicleFuel.values.map((fuel) => {
                return {
                    value: fuel,
                    text: LeadVehicleFuel.getTranslation(fuel),
                };
            });
        },

        noteOptions() {
            return [
                {
                    value: 'noAudioNote',
                    text: i18n.t('filters.media.noAudioNote'),
                },
                {
                    value: 'noFileAttached',
                    text: i18n.t('filters.media.noFileAttached'),
                },
                {
                    value: 'audioNote',
                    text: i18n.t('filters.media.audioNote'),
                },
                {
                    value: 'fileAttached',
                    text: i18n.t('filters.media.fileAttached'),
                },
            ];
        },
        intentionsOptions() {
            return union(
                uniq(
                    map(
                        filter(this.contextAccount.products, (p: any) => p.type === 'service'),
                        (p: any) => p.category
                    )
                ),
                ['problem']
            )
                .sort()
                .map((option: any) => {
                    return {
                        value: option,
                        text: i18n.t(`service.categories.${option}`),
                    };
                });
        },

        divisionOptions() {
            const baseDivision =
                this.dashboardType === 'service'
                    ? [Division.SERVICE]
                    : [Division.NEW, Division.USED];

            if (
                ['allLead', 'event', 'leadXpress', 'webBoost', 'phoneUp'].includes(
                    this.dashboardType
                )
            ) {
                baseDivision.push(Division.SERVICE);
            }

            const authUserHasDivision = (divisionId: number): boolean => {
                if (this.authUser.account.leads_other_division) {
                    return true;
                }
                return this.authUser.hasDivision(divisionId) || this.authUser.isAdmin();
            };

            return baseDivision.map((divisionId) => {
                return {
                    value: Division.getKey(divisionId),
                    condition: authUserHasDivision(divisionId),
                };
            });
        },

        followedOptions() {
            return [
                'none',
                'noCall',
                'noEmail',
                {
                    value: 'noSms',
                    condition: this.accountHasNioText,
                },
                {
                    value: 'noMessenger',
                    condition: this.accountHasMessenger,
                },
                {
                    value: 'noVideo',
                    condition: this.accountHasVideoConference,
                },
                'withCall',
                'withEmail',
                {
                    value: 'withSms',
                    condition: this.accountHasNioText,
                },
                {
                    value: 'withMessenger',
                    condition: this.accountHasMessenger,
                },
                {
                    value: 'withVideo',
                    condition: this.accountHasVideoConference,
                },
                'withAutomated',
                'noAutomated',
            ];
        },

        leadBankOptions() {
            return this.leadBanks.map((bank: any) => {
                return {
                    value: bank.text,
                    text: bank.text,
                };
            });
        },

        leadTypeOptions() {
            return [
                'email',
                'phone',
                'walk_in',
                'loyalty',
                'renewal',
                {
                    value: 'web_order',
                    condition: this.authUser.isAdmin() || this.authUser.hasWebOrder(),
                },
                {
                    value: 'event',
                    condition: ['walkIn', 'saleTable', 'allLead'].includes(this.dashboardType),
                },
                {
                    value: 'pre_booking',
                    condition:
                        this.divisionOptions.filter((division) => division.value == 'service')
                            .length == 1,
                },
                {
                    value: 'dms',
                    condition:
                        this.divisionOptions.filter((division) => division.value == 'service')
                            .length == 1,
                },
                {
                    value: 'sms',
                    condition: this.accountHasNioText,
                },
            ];
        },

        localeOptions() {
            return Locale.selectOptions('value', 'text');
        },

        presentedOptions() {
            if (this.dashboardType === 'service') {
                return [
                    {
                        value: 'presented',
                        text: i18n.t('general.yes'),
                    },
                    {
                        value: 'none',
                        text: i18n.t('general.no'),
                    },
                ];
            }

            return [
                {
                    value: 'presented',
                    text: i18n.t('filters.presented.presented'),
                },
                {
                    value: 'presentedOther',
                    text: i18n.t('filters.presented.presentedOther'),
                },
            ];
        },

        roadTestOptions() {
            if (this.dashboardType === 'service') {
                return [
                    {
                        value: 'roadTest',
                        text: i18n.t('general.yes'),
                    },
                    {
                        value: 'none',
                        text: i18n.t('general.no'),
                    },
                ];
            }

            return [
                {
                    value: 'roadTest',
                    text: i18n.t('filters.roadTest.roadTest'),
                },
                {
                    value: 'homeRoadTest',
                    text: i18n.t('filters.roadTest.homeRoadTest'),
                },
            ];
        },

        mediaOptions() {
            return [
                {
                    value: 'audio',
                    text: i18n.t('filters.media.audio'),
                },
                {
                    value: 'video',
                    text: i18n.t('filters.media.video'),
                },
            ];
        },

        provinceOptions() {
            return Province.getProvincesByCountry(this.contextAccount.country || 'CA')
                .map((province: string) => {
                    return {
                        value: province,
                        text: i18n.t(
                            Province.getTranslationKey(
                                this.contextAccount.country || 'CA',
                                province
                            )
                        ),
                    };
                })
                .sort((a: { value: string; text: string }, b: { value: string; text: string }) =>
                    a.text.localeCompare(b.text)
                );
        },

        countryOptions() {
            return Country.selectOptions('value', 'text');
        },

        saleTableDateOptions() {
            return [
                {
                    value: 'yes',
                    text: i18n.t('general.yes'),
                },
                {
                    value: 'no',
                    text: i18n.t('general.no'),
                },
                {
                    value: 'inside_dates',
                    text: i18n.t('general.insideDates'),
                    condition: this.dashboardType == 'saleTable',
                },
                {
                    value: 'outside_dates',
                    text: i18n.t('general.outsideDates'),
                    condition: this.dashboardType == 'saleTable',
                },
            ];
        },

        discountedOptions() {
            return [
                {
                    value: 'yes',
                    text: i18n.t('general.yes'),
                },
                {
                    value: 'no',
                    text: i18n.t('general.no'),
                },
                {
                    value: 'inside_dates',
                    text: i18n.t('general.insideDates'),
                    condition: ['saleTable', 'commercial'].includes(this.dashboardType),
                },
                {
                    value: 'outside_dates',
                    text: i18n.t('general.outsideDates'),
                    condition: ['saleTable', 'commercial'].includes(this.dashboardType),
                },
            ];
        },

        saleStateOptions() {
            const states = [
                'approved',
                'carryOver',
                'delivered',
                'deliveryCarryOver',
                'carryOverNotDelivered',
                'lost',
                'planned',
                'postponed',
                'refused',
                'sold',
                'waitingSale',
            ];

            if (this.hasClientCardField('process', 'discounted')) {
                states.push('discounted');
            }

            return states;
        },

        providerOptions() {
            const locale = i18n.getLocale();

            return this.contextAccount.providers
                .map((provider: any) => {
                    return {
                        value: provider.name.trim(),
                        text: (
                            getRegionalDisplayName(provider, locale, null, provider.name) ??
                            provider.name
                        ).trim(),
                    };
                })
                .sort((a: { text: string }, b: { text: string }) =>
                    a.text.trim().localeCompare(b.text.trim())
                );
        },

        hasHyundaiSupplier() {
            return this.contextAccount.hasHyundaiSupplier();
        },

        segmentOptions() {
            if (this.hasHyundaiSupplier) {
                return HyundaiSegments.selectOptions('value', 'text');
            }

            return [
                'other',
                'conquest',
                'loyalty',
                'notSold',
                'promo',
                'service',
                'reminder',
                'endWarranty',
                'endLcap',
                'endLnette',
                'csi',
                'noShow',
            ];
        },

        todoOptions() {
            return [
                {
                    value: 'late',
                    text: i18n.t('followUp.todo.late'),
                },
                {
                    value: TaskEventType.CALL,
                    text: i18n.t('followUp.todo.call'),
                },
                {
                    value: TaskEventType.EMAIL,
                    text: i18n.t('followUp.todo.email'),
                },
                {
                    value: TaskEventType.SMS,
                    text: i18n.t('followUp.todo.sms'),
                    condition: this.accountHasNioText,
                },
                {
                    value: TaskEventType.APPOINTMENT,
                    text: i18n.t('followUp.todo.appointment'),
                },
                {
                    value: TaskEventType.PHONE_APPOINTMENT,
                    text: i18n.t('followUp.todo.phoneAppointment'),
                },
                {
                    value: TaskEventType.TEST_DRIVE,
                    text: i18n.t('followUp.todo.testDrive'),
                },
                {
                    value: TaskEventType.VIRTUAL_APPOINTMENT,
                    text: i18n.t('followUp.todo.virtualAppointment'),
                    condition: this.accountHasVideoConference || this.hasHyundaiSupplier,
                },
                {
                    value: TaskEventType.DELIVERY,
                    text: i18n.t('followUp.todo.delivery'),
                },
                {
                    value: TaskEventType.OTHER,
                    text: i18n.t('followUp.todo.other'),
                },
                {
                    value: TaskEventType.CSI,
                    text: i18n.t('followUp.todo.csi'),
                    condition: this.contextAccount.hasOption('csi'),
                },
                {
                    value: 'automated',
                    text: i18n.t('followUp.todo.automated'),
                },
            ];
        },

        vehicleStateOptions() {
            return LeadVehicleState.values.map((state) => {
                return {
                    value: state,
                    text: LeadVehicleState.getTranslation(state),
                };
            });
        },

        contactMethodOptions() {
            const options = [
                'withEmail',
                'withoutEmail',
                'withPhone',
                'withoutPhone',
                'answered',
                'waiting',
                'withoutEmailAndPhone',
            ];

            if (this.accountHasNioText) {
                options.push('withMobile');
                options.push('withoutMobile');
                options.push('withoutEmailAndMobile');
            }

            return options;
        },

        columns(): Record<string, ColumnDefinition> {
            const columns = {
                'account:name': {
                    isValid: () => this.groupDisplay,
                    style: { width: '120px' },
                    title: i18n.t('general.accountColumn'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatAccountName(lead, true);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        options: this.accountOptions,
                    },
                },
                bulk_select: {
                    isValid: () => !this.groupDisplay,
                    style: { width: '42px' },
                    fixed: true,
                    mandatory: true,
                    filter: {
                        type: 'custom',
                    },
                },

                lead_navigation: {
                    isValid: () => this.customerProfileDashboardLayoutEnabled,
                    title: this.leadNavigationColumnTitle,
                    style: { width: '160px' },
                    fixed: true,
                    mandatory: true,
                    component: 'client-name',
                    filter: {
                        type: 'custom',
                    },
                },

                fullname: {
                    title: this.clientColumnTitle,
                    style: { width: '160px' },
                    mandatory: true,
                    component: this.customerProfileDashboardLayoutEnabled
                        ? 'customer-name'
                        : 'client-name',
                    filter: {
                        type: 'string',
                    },
                },

                date: {
                    title: 'Date',
                    style: { width: '100px' },
                    formater: (lead: any) => {
                        return formater.formatDate(
                            lead,
                            this.parsedStartDate,
                            this.parsedEndDate,
                            this.dashboardType
                        );
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterWithoutYearTooltip'),
                    tooltip: this.dateColumnTooltip,
                    filter: {
                        type: 'text',
                    },
                },

                address: {
                    title: i18n.t('clientCard.address'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatAddress(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                agents: {
                    isValid: () => isEmpty(this.contextGroup) && this.contextAccount.bdc_advisor,
                    style: { width: '125px' },
                    title: i18n.t('general.agent'),
                    component: 'agent',
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        sorting: false,
                        options: this.activeUsers,
                    },
                },

                advisors: {
                    isValid: () => isEmpty(this.contextGroup),
                    style: { width: '125px' },
                    title: i18n.t('general.advisor'),
                    component: 'advisor',
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        sorting: false,
                        options: this.activeUsers,
                    },
                },

                secondary_advisors: {
                    isValid: () =>
                        isEmpty(this.contextGroup) && this.contextAccount.secondary_advisor,
                    style: { width: '125px' },
                    title: i18n.t('general.secondaryAdvisor'),
                    component: 'secondary-advisor',
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        sorting: false,
                        options: this.activeUsers,
                    },
                },

                assignees: {
                    title: i18n.t('general.assignees'),
                    style: { width: '150px' },
                    component: 'assignees',
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        sorting: false,
                        options: this.activeUsers,
                    },
                },
                phone_appointment: {
                    isValid: () => this.hasClientCardField('performance', 'phone_appointment'),
                    title: `
                        <div class="flex items-center">
                            <span class="mr-2">
                                ${i18n.t('general.appointment')}
                            </span>
                            ${getIconMarkup('regular/phone-landline', {
                                class: 'event-icon',
                            })}
                        </div>
                    `,
                    sortTitle: i18n.t('general.appointment'),
                    style: { width: '75px' },
                    component: 'phone-appointment',
                    filter: {
                        multiple: true,
                        type: 'select',
                        baseTrans: 'filters.appointment',
                        options: this.phoneAppointmentOptions,
                        sorting: false,
                    },
                },

                appointment: {
                    isValid: () => this.hasClientCardField('performance', 'appointment'),
                    title: i18n.t('general.appointment'),
                    style: { width: '75px' },
                    component: 'appointment',
                    filter: {
                        multiple: true,
                        type: 'select',
                        baseTrans: 'filters.appointment',
                        options: this.appointmentOptions,
                        sorting: false,
                    },
                },
                approved: {
                    isValid: () => this.hasClientCardField('process', 'approved'),
                    title: i18n.t('commercial.approved', 1),
                    style: { width: '75px' },
                    component: 'approved',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                appt_confirmation: {
                    title: i18n.t('service.columns.confirmation'),
                    style: { width: '75px' },
                    component: 'appt-confirmation',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                available: {
                    isValid: () => this.hasClientCardField('process', 'available'),
                    title: i18n.t('clientCard.availableDate'),
                    style: { width: '90px' },
                    component: 'available',
                    filter: {
                        type: 'select',
                        withEmpty: this.dashboardType != 'saleTable',
                        multiple: this.dashboardType == 'saleTable',
                        options: this.saleTableDateOptions,
                        sorting: false,
                    },
                },

                paperwork: {
                    isValid: () => this.hasClientCardField('process', 'paperwork'),
                    title: i18n.t('clientCard.paperwork'),
                    style: { width: '120px' },
                    component: 'paperwork',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                average_spending: {
                    title: i18n.t('service.columns.averageSpending'),
                    style: { width: '120px' },
                    tooltip: i18n.t('clientCard.services.averageSpendingTooltip'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatAverageSpending(lead);
                    },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                be_back: {
                    isValid: () => this.hasClientCardField('performance', 'be_back'),
                    title: i18n.t('result.beBack'),
                    style: { width: '75px' },
                    component: 'be-back',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                birth_date: {
                    title: i18n.t('clientCard.birthDate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatBirthDate(lead);
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterWithoutYearTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                budget: {
                    isValid: () => this.hasClientCardField('general_info', 'budget'),
                    title: i18n.t('accounts.edit.budgetTextField'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatBudget(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                business: {
                    title: i18n.t('clientCard.business'),
                    style: { width: '80px' },
                    component: 'business',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },
                'event_campaigns:name': {
                    isValid: () => this.hasClientCardField('general_info', 'campaign'),
                    title: i18n.t('clientCard.event.event'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatCampaign(lead, CampaignType.EVENT);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                campaigns: {
                    isValid: () => this.contextAccount.hasOption('campaign'),
                    title: i18n.t('dashboards.campaigns'),
                    style: { width: '175px' },
                    verticalTruncating: true,
                    formater: (lead: any) => {
                        return formater.formatCampaigns(lead);
                    },
                    filter: {
                        type: 'select',
                        filterable: true,
                        multiple: true,
                        withNone: true,
                        options: this.contextAccount.campaigns
                            .filter((campaign: any) => campaign.creation_method === 'manual')
                            .map((campaign: any) => {
                                return {
                                    value: campaign.id,
                                    text: campaign.name,
                                    created_at: campaign.created_at,
                                };
                            }),
                        sorting: false,
                    },
                },

                campaigns_sent: {
                    valid:
                        this.contextAccount.hasOption('campaign') &&
                        useFeature.isEnabled('drilldown_campaign'),
                    title: i18n.t('dashboards.campaignsSent'),
                    style: { width: '175px' },
                    verticalTruncating: true,
                    formater: (lead: any) => {
                        return formater.formatCampaignsStatus(lead, 'sent');
                    },
                    filter: {
                        type: 'select',
                        filterable: true,
                        withEmpty: true,
                        optionType: 'bool',
                        sorting: false,
                    },
                },

                campaigns_delivered: {
                    valid:
                        this.contextAccount.hasOption('campaign') &&
                        useFeature.isEnabled('drilldown_campaign'),
                    title: i18n.t('dashboards.campaignsDelivered'),
                    style: { width: '175px' },
                    verticalTruncating: true,
                    formater: (lead: any) => {
                        return formater.formatCampaignsStatus(lead, 'delivered');
                    },
                    filter: {
                        type: 'select',
                        filterable: true,
                        withEmpty: true,
                        optionType: 'bool',
                        sorting: false,
                    },
                },

                campaigns_opened: {
                    valid:
                        this.contextAccount.hasOption('campaign') &&
                        useFeature.isEnabled('drilldown_campaign'),
                    title: i18n.t('dashboards.campaignsOpened'),
                    style: { width: '175px' },
                    verticalTruncating: true,
                    formater: (lead: any) => {
                        return formater.formatCampaignsStatus(lead, 'opened');
                    },
                    filter: {
                        type: 'select',
                        filterable: true,
                        withEmpty: true,
                        optionType: 'bool',
                        sorting: false,
                    },
                },

                campaigns_clicked: {
                    valid:
                        this.contextAccount.hasOption('campaign') &&
                        useFeature.isEnabled('drilldown_campaign'),
                    title: i18n.t('dashboards.campaignsClicked'),
                    style: { width: '175px' },
                    verticalTruncating: true,
                    formater: (lead: any) => {
                        return formater.formatCampaignsStatus(lead, 'clicked');
                    },
                    filter: {
                        type: 'select',
                        filterable: true,
                        withEmpty: true,
                        optionType: 'bool',
                        sorting: false,
                    },
                },

                campaigns_replied: {
                    valid:
                        this.contextAccount.hasOption('campaign') &&
                        useFeature.isEnabled('drilldown_campaign'),
                    title: i18n.t('dashboards.campaignsReplied'),
                    style: { width: '185px' },
                    verticalTruncating: true,
                    formater: (lead: any) => {
                        return formater.formatCampaignsStatus(lead, 'replied', true);
                    },
                    filter: {
                        type: 'select',
                        filterable: true,
                        withEmpty: true,
                        optionType: 'bool',
                        sorting: false,
                    },
                },

                campaigns_objective_reached: {
                    valid:
                        this.contextAccount.hasOption('campaign') &&
                        useFeature.isEnabled('drilldown_campaign'),
                    title: i18n.t('dashboards.campaignsObjectiveReached'),
                    style: { width: '185px' },
                    verticalTruncating: true,
                    formater: (lead: any) => {
                        return formater.formatCampaignsStatus(lead, 'objective_reached', true);
                    },
                    filter: {
                        type: 'select',
                        filterable: true,
                        withEmpty: true,
                        optionType: 'bool',
                        sorting: false,
                    },
                },

                imported_campaign: {
                    isValid: () => this.hasClientCardField('general_info', 'campaign'),
                    title: i18n.t('dashboards.importedCampaign'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatImportedCampaign(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                city: {
                    title: i18n.t('general.city'),
                    style: { width: '100px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatCity(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                civility: {
                    title: i18n.t('clientCard.civility'),
                    style: { width: '100px' },
                    formater: (lead: any) => {
                        return formater.formatCivility(lead);
                    },
                    filter: {
                        type: 'select',
                        withNone: true,
                        options: this.civilityOptions,
                    },
                },

                code: {
                    title: i18n.t('service.columns.code'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatCode(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'commercial_products:pellicule': {
                    isValid: () => this.hasAccountProduct('pellicule'),
                    title: i18n.t('clientCard.products.pellicule'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:wheel_protection': {
                    isValid: () => this.hasAccountProduct('wheel_protection'),
                    title: i18n.t('clientCard.products.wheel_protection'),
                    style: { width: '160px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:mouse_repellent': {
                    isValid: () => this.hasAccountProduct('mouse_repellent'),
                    title: i18n.t('clientCard.products.mouse_repellent'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:paint_treatment': {
                    isValid: () => this.hasAccountProduct('paint_treatment'),
                    title: i18n.t('clientCard.products.paint_treatment'),
                    style: { width: '150px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:roof_treatment': {
                    isValid: () => this.hasAccountProduct('roof_treatment'),
                    title: i18n.t('clientCard.products.roof_treatment'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:leather_tissu_interior_treatment': {
                    isValid: () => this.hasAccountProduct('leather_tissu_interior_treatment'),
                    title: i18n.t('clientCard.products.leather_tissu_interior_treatment'),
                    style: { width: '200px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:niotext': {
                    isValid: () => this.hasAccountProduct('niotext'),
                    title: this.contextAccount?.sms_module
                        ? i18n.t('clientCard.products.smsModule')
                        : i18n.t('clientCard.products.niotext'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:walk_in': {
                    isValid: () => this.hasAccountProduct('walk_in'),
                    title: i18n.t('clientCard.products.walk_in'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:sale_table': {
                    isValid: () => this.hasAccountProduct('sale_table'),
                    title: i18n.t('clientCard.products.sale_table'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:in_turn': {
                    isValid: () => this.hasAccountProduct('in_turn'),
                    title: i18n.t('clientCard.products.in_turn'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:renewal': {
                    isValid: () => this.hasAccountProduct('renewal'),
                    title: i18n.t('clientCard.products.renewal'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:event': {
                    isValid: () => this.hasAccountProduct('event'),
                    title: i18n.t('clientCard.products.event'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:service': {
                    isValid: () => this.hasAccountProduct('service'),
                    title: i18n.t('clientCard.products.service'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:financing': {
                    isValid: () => this.hasAccountProduct('financing'),
                    title: i18n.t('clientCard.products.financing'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:ins_filling': {
                    isValid: () => this.hasAccountProduct('ins_filling'),
                    title: i18n.t('clientCard.products.ins_filling'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:extended_warranty': {
                    isValid: () => this.hasAccountProduct('extended_warranty'),
                    title: i18n.t('clientCard.products.extended_warranty'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:starter': {
                    isValid: () => this.hasAccountProduct('starter'),
                    title: i18n.t('clientCard.products.starter'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:window_tint': {
                    isValid: () => this.hasAccountProduct('window_tint'),
                    title: i18n.t('clientCard.products.window_tint'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:pre_paid_maintenance': {
                    isValid: () => this.hasAccountProduct('pre_paid_maintenance'),
                    title: i18n.t('clientCard.products.pre_paid_maintenance'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:seat_protection': {
                    isValid: () => this.hasAccountProduct('seat_protection'),
                    title: i18n.t('clientCard.products.seat_protection'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:pef': {
                    isValid: () => this.hasAccountProduct('pef'),
                    title: i18n.t('clientCard.products.pef'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:pep': {
                    isValid: () => this.hasAccountProduct('pep'),
                    title: i18n.t('clientCard.products.pep'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:other': {
                    isValid: () => this.hasAccountProduct('other'),
                    title: i18n.t('clientCard.products.other'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:ins_rental': {
                    isValid: () => this.hasAccountProduct('ins_rental'),
                    title: i18n.t('clientCard.products.ins_rental'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:ins_invalidity': {
                    isValid: () => this.hasAccountProduct('ins_invalidity'),
                    title: i18n.t('clientCard.products.ins_invalidity'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:ins_health': {
                    isValid: () => this.hasAccountProduct('ins_health'),
                    title: i18n.t('clientCard.products.ins_health'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:ins_life': {
                    isValid: () => this.hasAccountProduct('ins_life'),
                    title: i18n.t('clientCard.products.ins_life'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:rustproofing': {
                    isValid: () => this.hasAccountProduct('rustproofing'),
                    title: i18n.t('clientCard.products.rustproofing'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:chiselling': {
                    isValid: () => this.hasAccountProduct('chiselling'),
                    title: i18n.t('clientCard.products.chiselling'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },
                'commercial_products:anti_theft': {
                    isValid: () => this.hasAccountProduct('anti_theft'),
                    title: i18n.t('clientCard.products.anti_theft'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:windshield_treatment': {
                    isValid: () => this.hasAccountProduct('windshield_treatment'),
                    title: i18n.t('clientCard.products.windshield_treatment'),
                    style: { width: '160px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:burn_protection': {
                    isValid: () => this.hasAccountProduct('burn_protection'),
                    title: i18n.t('clientCard.products.burn_protection'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial_products:flame_quard_protection': {
                    isValid: () => this.hasAccountProduct('flame_quard_protection'),
                    title: i18n.t('clientCard.products.flame_quard_protection'),
                    style: { width: '150px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'commercial:': {
                    title: i18n.t('general.commercial'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return lead.commercialUser.id ? lead.commercialUser.fullName : '-';
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.activeUsers,
                    },
                },

                commercial_meeting: {
                    title: i18n.t('commercial.meeting'),
                    style: { width: '120px' },
                    component: 'commercial-meeting',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                commercial_no_need: {
                    title: i18n.t('commercial.noNeed'),
                    style: { width: '120px' },
                    component: 'commercial-no-need',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'customer:casl_consent_limit_date': {
                    title: i18n.t('consent.consentExpiration'),
                    style: { width: '165px' },
                    truncating: true,
                    formater: formater.formatCaslConsentLimitDate,
                    filter: {
                        type: 'select',
                        selectAll: false,
                        sorting: false,
                        withEmpty: true,
                        options: this.caslConsentLimitDateOptions,
                    },
                },

                'customer:casl_consent_status': {
                    title: i18n.t('consent.consentStatus'),
                    style: { width: '135px' },
                    component: 'ConsentStatus',
                    filter: {
                        type: 'select',
                        baseTrans: 'consent.states',
                        withEmpty: true,
                        options: [
                            CaslConsentState.CONFIRMED,
                            CaslConsentState.TEMPORARY,
                            CaslConsentState.EXPIRED,
                        ],
                    },
                },

                contact_method: {
                    title: 'Contact',
                    style: {
                        width: this.accountHasNioText ? '135px' : '105px',
                    },
                    baseClass: 'whitespace-nowrap',
                    component: 'contact-method',
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'client',
                        options: this.contactMethodOptions,
                    },
                },

                contacted_by_all_main_methods: {
                    title: i18n.t('dashboards.contactedByAllMainMethods'),
                    style: { width: '110px' },
                    formater: (lead: any) => {
                        return formater.formatBoolean(lead, 'contacted_by_all_main_methods');
                    },
                    tooltip: i18n.t('dashboards.contactedByAllMainMethodsTooltip'),
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                created_by: {
                    title: i18n.t('dashboards.createdBy'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return lead.creatorUser.id ? lead.creatorUser.fullName : '-';
                    },
                    filter: {
                        sorting: false,
                        type: 'select',
                        multiple: true,
                        options: this.activeUsersAndSupplierUsers,
                        withNone: true,
                        noneLabel: 'Activix CRM',
                    },
                },

                updated_by: {
                    title: i18n.t('dashboards.updatedBy'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return lead.updaterUser.id ? lead.updaterUser.fullName : '-';
                    },
                    filter: {
                        sorting: false,
                        type: 'select',
                        multiple: true,
                        options: this.activeUsersAndSupplierUsers,
                        withNone: true,
                        noneLabel: 'Activix CRM',
                    },
                },

                created_method: {
                    isValid: () => this.hasClientCardField('general_info', 'created_method'),
                    title: i18n.t('dashboards.createdMethodString'),
                    style: { width: '100px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatCreatedMethod(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'createdMethods',
                        options: [
                            CreatedMethod.ACTIVIX,
                            CreatedMethod.ACURA_CANADA,
                            CreatedMethod.API,
                            CreatedMethod.AUTO_RENEWAL,
                            CreatedMethod.CDK,
                            CreatedMethod.CT_WIZARD,
                            CreatedMethod.DEALER_VU,
                            CreatedMethod.EASYDEAL,
                            CreatedMethod.F_C_A_DIGITAL,
                            CreatedMethod.FORD_DIRECT,
                            CreatedMethod.GM_CANADA,
                            CreatedMethod.HONDA_CANADA,
                            CreatedMethod.KIA_DIGITAL,
                            CreatedMethod.M_B_C_A_DIGITAL,
                            CreatedMethod.MANUAL,
                            CreatedMethod.MANUAL_IMPORT,
                            CreatedMethod.MESSENGER,
                            CreatedMethod.MITSUBISHI,
                            CreatedMethod.N_C_I_DIGITAL,
                            this.contextAccount?.sms_module
                                ? {
                                      value: CreatedMethod.NIOTEXT,
                                      text: i18n.t('createdMethods.sms'),
                                  }
                                : CreatedMethod.NIOTEXT,
                            CreatedMethod.PBS,
                            CreatedMethod.PHONE_SYSTEM,
                            CreatedMethod.POLARIS_DIGITAL,
                            CreatedMethod.PORSCHE_DIGITAL,
                            CreatedMethod.PORSCHE_FINDER,
                            CreatedMethod.TAQ_AUTO,
                            CreatedMethod.SCAN,
                            CreatedMethod.SCRAPER,
                            CreatedMethod.SERTI,
                            CreatedMethod.STELLANTIS_DIGITAL,
                            CreatedMethod.HARLEY_DAVIDSON_DIGITAL,
                            CreatedMethod.POLESTAR,
                            CreatedMethod.MASERATI_DIGITAL,
                            CreatedMethod.ONE_EIGHTY,
                            CreatedMethod.HYUNDAI,
                            CreatedMethod.CALL_LOG,
                            CreatedMethod.VOLKSWAGEN,
                            CreatedMethod.BMW_CANADA,
                            CreatedMethod.AUTOSYNC,
                            CreatedMethod.REYNOLDS,
                            CreatedMethod.AUDI_CANADA,
                            CreatedMethod.JAGUAR_LAND_ROVER_DIGITAL,
                            CreatedMethod.BOMBARDIER_RECREATIONAL_PRODUCTS,
                            CreatedMethod.VOLVO,
                            CreatedMethod.SUBARU,
                        ],
                    },
                },

                csi: {
                    isValid: () =>
                        this.hasClientCardField('process', 'csi') &&
                        this.contextAccount.hasOption('csi'),
                    title: i18n.t('clientCard.csi'),
                    style: { width: '75px' },
                    component: 'csi',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                csi_date: {
                    isValid: () =>
                        this.hasClientCardField('process', 'csi') &&
                        this.contextAccount.hasOption('csi'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatCsiDate(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },
                dealer_tour: {
                    isValid: () => this.hasClientCardField('performance', 'dealer_tour'),
                    title: i18n.t('clientCard.dealerTour'),
                    style: { width: '75px' },
                    component: 'dealer-tour',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                deliverable: {
                    isValid: () => this.hasClientCardField('process', 'deliverable'),
                    title: i18n.t('clientCard.deliverableDate'),
                    style: { width: '135px' },
                    component: 'deliverable',
                    filter: {
                        type: 'select',
                        withEmpty: this.dashboardType != 'saleTable',
                        multiple: this.dashboardType == 'saleTable',
                        options: this.saleTableDateOptions,
                        sorting: false,
                    },
                },

                delivered: {
                    isValid: () => this.hasClientCardField('process', 'delivered'),
                    title: i18n.t('result.delivered'),
                    style: { width: '125px' },
                    component: 'delivered',
                    filter: {
                        type: 'select',
                        withEmpty: this.dashboardType != 'saleTable',
                        multiple: this.dashboardType == 'saleTable',
                        options: this.saleTableDateOptions,
                        sorting: false,
                    },
                },

                'delivered_by:': {
                    isValid: () => this.contextAccount.hasOption('delivered_by'),
                    title: i18n.t('clientCard.deliveredBy'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return lead.deliveredByUser.id ? lead.deliveredByUser.fullName : '-';
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.notServiceUsers,
                    },
                },

                delivery: {
                    isValid: () => this.hasClientCardField('process', 'delivery'),
                    title: i18n.t('clientCard.deliveryDate'),
                    style: { width: '95px' },
                    component: 'delivery',
                    filter: {
                        type: 'select',
                        withEmpty: this.dashboardType != 'saleTable',
                        multiple: this.dashboardType == 'saleTable',
                        options: this.saleTableDateOptions,
                        sorting: false,
                    },
                },

                deposit: {
                    isValid: () => this.hasClientCardField('performance', 'deposit'),
                    title: i18n.t('result.deposit'),
                    style: { width: '75px' },
                    component: 'deposit',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                write_up: {
                    isValid: () => this.hasClientCardField('performance', 'write_up'),
                    title: i18n.t('clientCard.writeUp'),
                    style: { width: '75px' },
                    component: 'writeUp',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                discounted_date: {
                    title: i18n.t('clientCard.discounted', 1),
                    style: { width: '85px' },
                    component: 'discounted',
                    filter: {
                        type: 'select',
                        options: this.discountedOptions,
                        sorting: false,
                        withEmpty: !['saleTable', 'commercial'].includes(this.dashboardType),
                        multiple: ['saleTable', 'commercial'].includes(this.dashboardType),
                    },
                },

                'division:name': {
                    isValid: () => this.hasClientCardField('general_info', 'division'),
                    title: 'Division',
                    style: { width: '100px' },
                    mandatory: true,
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatDivision(lead);
                    },
                    filter: {
                        type: 'select',
                        baseTrans: 'divisions',
                        multiple: true,
                        withNone: true,
                        selectAll: true,
                        options: this.divisionOptions,
                    },
                },

                do_not_market: {
                    isValid: () => this.hasClientCardField('general_info', 'do_not_market'),
                    title: i18n.t('clientCard.doNotMarket'),
                    style: { width: '100px' },
                    formater: (lead: any) => {
                        return formater.formatBoolean(lead, 'do_not_market');
                    },
                    filter: {
                        type: 'select',
                        optionType: 'bool',
                    },
                },

                end_service_date: {
                    title: i18n.t('clientCard.endServiceDate'),
                    style: { width: '140px' },
                    formater: (lead: any) => {
                        return (
                            asLocale(lead.end_service_date, 'end_service_date').humanShort() || '-'
                        );
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                evaluated: {
                    title: i18n.t('history.lead.evaluated'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatBoolean(lead, 'evaluated');
                    },
                    filter: {
                        type: 'select',
                        optionType: 'bool',
                    },
                },

                audio_notes_files: {
                    title: i18n.t('clientCard.audioNotesFiles'),
                    style: { width: '175px' },
                    component: 'noteAudioFiles',
                    filter: {
                        type: 'select',
                        multiple: true,
                        options: this.noteOptions,
                    },
                },

                'exchange_vehicles:accidented_damage_cost': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('general_info', 'accidented_damage_cost'),
                    title: i18n.t('clientCard.vehicles.accidented_damage_cost'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeAccidented(lead);
                    },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'exchange_vehicles:allowed_mileage': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'allowed_mileage'),
                    title: i18n.t('clientCard.vehicles.allowed_mileage'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeAllowedKm(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:balance': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'balance'),
                    title: i18n.t('clientCard.vehicles.balance'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeBalance(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:residual': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'residual'),
                    title: i18n.t('clientCard.vehicles.residual'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeResidual(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:bank:name': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title:
                        String(i18n.t('clientCard.institutionVehicle')) +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleInstitutionBank(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.leadBankOptions,
                    },
                },

                'exchange_vehicles:calculator_appraiser': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('history.vehicle.calculatorAppraiser'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeCalculatorAppraiser(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:category': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'category'),
                    title:
                        String(i18n.t('clientCard.vehicles.category')) +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleCategory(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'clientCard.vehicles',
                        options: [
                            'automotive',
                            'boat',
                            'truck',
                            'van',
                            'hybride',
                            'motorcycle',
                            'watercraft',
                            'snowmobile',
                            'motorized',
                            'caravan',
                            'trailer',
                            'utility',
                            'atv',
                            'suv',
                            'bike',
                            'mechanical',
                        ],
                    },
                },

                'exchange_vehicles:category_rv': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'category_rv'),
                    title:
                        String(i18n.t('clientCard.vehicles.categoryRV')) +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleCategoryRV(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'clientCard.vehicles',
                        options: [
                            'class_a_diesel',
                            'hybride',
                            'motorized_a',
                            'motorized_b',
                            'motorized_c',
                            'travel_trailer',
                            'fifth_wheel',
                            'trailer_park',
                            'tent_trailer',
                        ],
                    },
                },

                'exchange_vehicles:color_exterior': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'color_exterior'),
                    title:
                        String(i18n.t('clientCard.vehicles.color')) +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleColor(lead, 'exchange', 'color_exterior');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:color_interior': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'color_interior'),
                    title: `${
                        i18n.t('accounts.edit.colorInterior') + String(i18n.t('general.currentAbr'))
                    }`,
                    style: { width: '150px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleColor(lead, 'exchange', 'color_interior');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:condition': {
                    title: `${
                        i18n.t('accounts.edit.condition') + String(i18n.t('general.currentAbr'))
                    }`,
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeCondition(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.conditionOptions,
                    },
                },

                'exchange_vehicles:end_contract_date': {
                    // TODO TEST NAMING END_CONTRACT VS END_CONTRACT_DATE
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'end_contract_date'),
                    title: `${
                        i18n.t('clientCard.vehicles.contractEnd') +
                        String(i18n.t('general.currentAbr'))
                    }`,
                    style: { width: '150px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleEndContract(lead, 'exchange');
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:end_warranty_date': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title:
                        i18n.t('clientCard.vehicles.endWarrantyDate') +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '130px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleEndWarrantyDate(lead);
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:sold_at': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'sold_date'),
                    title: i18n.t('accounts.edit.soldDate') + String(i18n.t('general.currentAbr')),
                    style: { width: '130px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleSoldDate(lead);
                    },
                    tooltip: i18n.t('general.currentVehicle'),
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:calculated_equity': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('clientCard.vehicles.equity'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeEquity(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:extended_warranty': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: `${
                        i18n.t('clientCard.vehicleExchangeExtendedWarranty') +
                        String(i18n.t('general.currentAbr'))
                    }`,
                    style: { width: '130px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeExtendedWarranty(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:frequency': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'frequency'),
                    title: i18n.t('clientCard.vehicles.frequency'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeFrequency(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'filters.vehicle_frequency',
                        options: ['one_payment', 'weekly', 'two_weeks', 'bi_monthly', 'monthly'],
                    },
                },

                'exchange_vehicles:fuel': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'fuel'),
                    title:
                        i18n.t('clientCard.vehicles.fuel') + String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleFuel(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.fuelOptions,
                    },
                },

                'exchange_vehicles:intention': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'intention'),
                    title: i18n.t('clientCard.vehicles.intention'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeIntention(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'clientCard.vehicles',
                        options: [
                            'renewal',
                            'refinance',
                            'buyBack',
                            'return',
                            'refuseToRenew',
                            'leaseTransfer',
                        ],
                    },
                },

                'exchange_vehicles:length_max': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'length'),
                    title: `${i18n.t('clientCard.vehicles.length')} max.${i18n.t(
                        'general.currentAbr'
                    )}`,
                    style: { width: '125px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleLength(lead, 'exchange', 'max');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:length_min': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'length'),
                    title: `${i18n.t('clientCard.vehicles.length')} min.${i18n.t(
                        'general.currentAbr'
                    )}`,
                    style: { width: '125px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleLength(lead, 'exchange', 'min');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:license_plate': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('clientCard.vehicleExchangeLicensePlate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeLicensePlate(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'exchange_vehicles:make': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('general.make') + String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleMake(lead, 'exchange');
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'exchange_vehicles:mechanical': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'category'),
                    title:
                        i18n.t('clientCard.vehicles.mechanical') +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleMechanical(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'clientCard.vehicles',
                        options: [
                            'generator',
                            'outside',
                            'water_pump',
                            'blower',
                            'border_size',
                            'motoculteur',
                            'mower',
                        ],
                    },
                },

                'exchange_vehicles:mileage': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'mileage'),
                    title: i18n.t('clientCard.vehicles.mileage'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeMileage(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:mileage_excess_percentage': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'mileage_excess'),
                    title: i18n.t('clientCard.vehicles.mileageExcess'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeMileageExcess(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:mo_remaining': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('clientCard.vehicles.monthsRemaining', 2),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeMoRemaining(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:modality': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'modality'),
                    title:
                        i18n.t('clientCard.vehicles.modality') +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleModality(lead, this.dashboardType, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'clientCard.vehicles',
                        options: ['financing', 'leasing', 'cash'],
                    },
                },

                'exchange_vehicles:model': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('general.model') + String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleModel(lead, 'exchange');
                    },
                    filter: {
                        type: this.accountHasVehiclesModelDropdownOption ? 'select' : 'string',
                        multiple: true,
                        withNone: true,
                        options: () => this.accountVehicleModels.exchange || [],
                        onOpen: () => {
                            if (!this.accountVehicleModelsLoaded) {
                                this.fetchAccountVehicleModels();
                            }
                        },
                    },
                },

                'exchange_vehicles:payment': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'payment'),
                    title: i18n.t('clientCard.vehicles.payment'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangePayment(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:payment_with_tax': {
                    isValid: () => this.hasClientCardField('exchange_vehicle', 'payment_with_tax'),
                    title:
                        i18n.t('clientCard.vehicles.paymentWithTax') +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '160px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangePaymentWithTax(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:price': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'price'),
                    title: i18n.t('general.price') + String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangePrice(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:purchase_date': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('clientCard.vehicleExchangePurchaseDate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangePurchaseDate(lead);
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:rate': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('accounts.edit.rate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleRate(lead, 'exchange');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:recall': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('clientCard.vehicleExchangeRecall'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeRecall(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:sold_by': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'sold_by'),
                    title: i18n.t('renewal.soldBy'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeSoldBy(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'exchange_vehicles:term': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('clientCard.vehicles.term'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleTerm(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        options: LeadVehicleTerm.selectOptions('value', 'text'),
                        optionType: 'entities',
                        withNone: true,
                    },
                },

                'exchange_vehicles:tire_type': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title:
                        i18n.t('clientCard.vehicles.tire_type') +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '100px' },
                    tooltip: i18n.t('clientCard.vehicles.tireTypeTooltip'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleTireType(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: [
                            {
                                value: 'summer',
                                text: i18n.t('clientCard.vehicles.tireType.summer'),
                            },
                            {
                                value: 'winter',
                                text: i18n.t('clientCard.vehicles.tireType.winter'),
                            },
                        ],
                    },
                },

                'exchange_vehicles:trade_type': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.contextAccount.trade_report &&
                        (this.authUser.trade_report || this.authUser.isAdmin()),
                    title: i18n.t('clientCard.vehicles.trade_type'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeTradeType(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'clientCard.vehicles.tradeTypes',
                        withNone: true,
                        options: [
                            'retail',
                            'wholesale',
                            'return',
                            'recycled',
                            'lost',
                            'excluded',
                            'other',
                        ],
                    },
                },

                'exchange_vehicles:vehicle_state': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'vehicle_state'),
                    title: i18n.t('clientCard.vehicleState') + String(i18n.t('general.currentAbr')),
                    style: { width: '150px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleState(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.vehicleStateOptions,
                    },
                },

                'exchange_vehicles:transmission': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'transmission'),
                    title:
                        i18n.t('service.categories.transmission') +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleTransmission(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'clientCard.vehicles',
                        options: ['auto', 'manual', 'sequential'],
                    },
                },

                'exchange_vehicles:version': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title:
                        i18n.t('clientCard.vehicles.version') +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleTrim(lead, 'exchange');
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'exchange_vehicles:value': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'value'),
                    title: i18n.t('clientCard.vehicles.value'),
                    style: { width: '100px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeValue(lead);
                    },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'exchange_vehicles:actual_value': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.contextAccount.trade_report &&
                        this.hasClientCardField('exchange_vehicle', 'actual_value'),
                    title: i18n.t('clientCard.vehicles.actual_value'),
                    tooltip: i18n.t('general.currentVehicle'),
                    style: { width: '100px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleActualValue(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'exchange_vehicles:calculator_value': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.calculatorValueVisible &&
                        (this.contextAccount.hasSupplier(SupplierId.VAUTO) ||
                            this.contextAccount.hasSupplier(SupplierId.AUTOPROPULSION)),
                    title:
                        i18n.t('clientCard.vehicles.calculator_value') +
                        String(i18n.t('general.currentAbr')),
                    style: { width: '100px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: false,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeSupplierValue(lead);
                    },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'exchange_vehicles:vin': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'vin'),
                    title: i18n.t('clientCard.vehicles.vin') + String(i18n.t('general.currentAbr')),
                    style: { width: '150px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleVin(lead, 'exchange');
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'exchange_vehicles:warranty': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'warranty'),
                    title: i18n.t('clientCard.vehicles.warranty'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleExchangeWarranty(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'exchange_vehicles:weight': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('exchange_vehicle', 'weight'),
                    title:
                        i18n.t('clientCard.vehicles.weight') + String(i18n.t('general.currentAbr')),
                    style: { width: '100px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleWeight(lead, 'exchange');
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'exchange_vehicles:year': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('general.year') + String(i18n.t('general.currentAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.currentVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleYear(lead, 'exchange');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        selectAll: false,
                        optionType: 'years',
                    },
                },
                followed: {
                    title: i18n.t('general.followUp'),
                    style: {
                        width: this.accountHasMessenger ? '190px' : '160px',
                    },
                    component: 'followed',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        baseTrans: 'followUp',
                        options: this.followedOptions,
                    },
                },

                followed_by: {
                    title: i18n.t('general.followBy'),
                    style: { width: '190px' },
                    component: 'followed-by',
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: false,
                        sorting: false,
                        options: this.activeUsers,
                    },
                },

                form: {
                    isValid: () => this.hasClientCardField('general_info', 'lead_form'),
                    title: i18n.t('clientCard.leadForm'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatLeadForm(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                gas: {
                    isValid: () => this.hasClientCardField('process', 'gas'),
                    title: i18n.t('clientCard.gas'),
                    style: { width: '75px' },
                    component: 'gas',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'import:original_file': {
                    title: i18n.t('general.importFile'),
                    style: { width: '150px' },
                    tooltip: i18n.t('dashboards.importFileTooltip'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatImportFile(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                inspected: {
                    isValid: () => this.hasClientCardField('process', 'inspected'),
                    title: i18n.t('clientCard.inspected'),
                    style: { width: '75px' },
                    component: 'inspected',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                invoiced: {
                    title: i18n.t('clientCard.invoiced'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatInvoiced(lead);
                    },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                keyword: {
                    isValid: () => this.hasClientCardField('general_info', 'keyword'),
                    title: i18n.t('clientCard.keyword'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatKeyword(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                last_presented_date: {
                    title: i18n.t('service.columns.lastPresentedDate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatLastPresentedDate(lead);
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                'latest_comment:content': {
                    title: i18n.t('general.note', 2),
                    style: { width: '250px' },
                    filter: {
                        type: 'string',
                    },
                },

                'lead_bank:name': {
                    isValid: () => this.hasClientCardField('process', 'lead_bank'),
                    title: i18n.t('clientCard.institution'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatInstitutionBank(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        sorting: true,
                        options: this.leadBankOptions,
                    },
                },

                lead_state: {
                    title: i18n.t('dashboards.leadStateString'),
                    style: { width: '150px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatLeadState(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'dashboards.leadState',
                        withNone: true,
                        options: ['toCheck', 'merged', 'associated', 'bothStates'],
                    },
                },

                'lead_type:name': {
                    isValid: () => this.hasClientCardField('general_info', 'lead_type'),
                    title: i18n.t('client.type'),
                    style: { width: '100px' },
                    formater: (lead: any) => {
                        return formater.formatType(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'filters.lead_type',
                        options: this.leadTypeOptions,
                    },
                },

                listening: {
                    title: i18n.t('general.media'),
                    style: { width: '100px' },
                    component: 'communication-media',
                    filter: {
                        type: 'select',
                        withNone: true,
                        multiple: true,
                        sorting: false,
                        options: this.mediaOptions,
                    },
                },

                locale: {
                    title: i18n.t('general.lang'),
                    style: { width: '80px' },
                    formater: (lead: any) => {
                        return formater.formatLocale(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        options: this.localeOptions,
                    },
                },

                loyalty: {
                    title: i18n.t('clientCard.loyalty'),
                    style: { width: '75px' },
                    component: 'loyalty',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                maintenance: {
                    title: i18n.t('service.columns.maintenance'),
                    style: { width: '90px' },
                    component: 'service-maintenance',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                next_presented_date: {
                    title: i18n.t('service.columns.nextPresentedDate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatNextPresentedDate(lead);
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                non_consumer_initiated: {
                    title: i18n.t('history.lead.nonConsumerInitiated'),
                    style: { width: '210px' },
                    formater: (lead: any) => {
                        return formater.formatBoolean(lead, 'non_consumer_initiated');
                    },
                    filter: {
                        type: 'select',
                        optionType: 'bool',
                    },
                },

                open_work_order_date: {
                    title: i18n.t('clientCard.openWorkOrderDate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return (
                            asLocale(
                                lead.open_work_order_date,
                                'open_work_order_date'
                            ).humanShort() || '-'
                        );
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                work_order_partial_closure_date: {
                    title: i18n.t('clientCard.workOrderPartialClosureDate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return (
                            asLocale(
                                lead.work_order_partial_closure_date,
                                'work_order_partial_closure_date'
                            ).humanShort() || '-'
                        );
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                work_order_closure_date: {
                    title: i18n.t('clientCard.workOrderClosureDate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return (
                            asLocale(
                                lead.work_order_closure_date,
                                'work_order_closure_date'
                            ).humanShort() || '-'
                        );
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                oversale: {
                    title: i18n.t('service.columns.overSale'),
                    style: { width: '75px' },
                    component: 'service-over-sale',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                phone_destination: {
                    title: i18n.t('result.destination'),
                    style: { width: '120px' },
                    fixed: true,
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatPhoneDestination(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                phone_origin: {
                    title: i18n.t('result.origin'),
                    style: { width: '120px' },
                    fixed: true,
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatPhoneOrigin(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                planned_pick_up_date: {
                    title: i18n.t('clientCard.plannedPickUpDate'),
                    style: { width: '140px' },
                    formater: (lead: any) => {
                        return (
                            asLocale(
                                lead.planned_pick_up_date,
                                'planned_pick_up_date'
                            ).humanShort() || '-'
                        );
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                postal_code: {
                    title: i18n.t(`general.postalCode.${this.contextAccount.country || 'CA'}`),
                    style: { width: '90px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatPostalCode(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                prepaid: {
                    title: i18n.t('clientCard.prepaid'),
                    style: { width: '75px' },
                    component: 'prepaid',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                prepared: {
                    isValid: () => this.hasClientCardField('process', 'prepared'),
                    title: i18n.t('clientCard.prepared'),
                    style: { width: '75px' },
                    component: 'prepared',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                prepared_work_order: {
                    isValid: () => this.hasClientCardField('process', 'prepared_work_order'),
                    title: i18n.t('clientCard.prepared_work_order'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatProgressWorkOrder(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                presented: {
                    isValid: () => this.hasClientCardField('performance', 'presented'),
                    title: i18n.t('result.presentedColumn'),
                    style: { width: '75px' },
                    component: 'presented',
                    filter: {
                        multiple: this.dashboardType !== 'service',
                        type: 'select',
                        options: this.presentedOptions,
                        withNone: this.dashboardType !== 'service',
                        withEmpty: this.dashboardType === 'service',
                        sorting: false,
                    },
                },

                progress_state: {
                    isValid: () => this.hasClientCardField('process', 'progress_state'),
                    title: i18n.t('clientCard.state'),
                    style: { width: '120px' },
                    tooltip: i18n.t('saleTable.progressStateTooltip'),
                    formater: (lead: any) => {
                        return formater.formatProgressState(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'commercial',
                        options: ProgressState.values,
                    },
                },

                promised_date: {
                    title: i18n.t('clientCard.promisedDate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return asLocale(lead.promised_date, 'promised_date').humanShort() || '-';
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                provider: {
                    title: i18n.t('leadFilter.provider'),
                    style: { width: '175px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatProvider(lead, this.providerOptions);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.providerOptions,
                    },
                },

                province: {
                    title: i18n.t(`general.province.${this.contextAccount.country || 'CA'}`),
                    style: { width: '100px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatProvince(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.provinceOptions,
                    },
                },
                country: {
                    title: i18n.t('general.country'),
                    style: { width: '100px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatCountry(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.countryOptions,
                    },
                },

                qualification: {
                    isValid: () => this.hasClientCardField('performance', 'qualification'),
                    title: i18n.t('clientCard.qualification'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatQualification(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                rating: {
                    isValid: () => this.hasClientCardField('general_info', 'rating'),
                    title: i18n.t('clientCard.rating'),
                    style: { width: '90px' },
                    tooltip: i18n.t('clientCard.ratingTooltip'),
                    component: 'rating',
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: [1, 2, 3, 4, 5].map((rating) => {
                            return {
                                value: rating,
                                text: rating,
                            };
                        }),
                    },
                },

                referrer: {
                    isValid: () => this.hasClientCardField('general_info', 'referrer'),
                    title: i18n.t('clientCard.referrer'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatReferrer(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                refinanced: {
                    isValid: () => this.hasClientCardField('performance', 'refinanced'),
                    title: i18n.t('dashboards.performance.refinanced'),
                    style: { width: '75px' },
                    component: 'refinanced',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                repair_date: {
                    title: i18n.t('clientCard.repairDate'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return asLocale(lead.repair_date, 'repair_date').humanShort() || '-';
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                renewal_alert_date: {
                    isValid: () =>
                        !this.contextAccount.isCustom() && this.contextAccount.equity_alert,
                    title: i18n.t('dashboards.alertDate'),
                    style: { width: '120px' },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    component: 'renewal-alert-notification-date',
                    filter: {
                        type: 'text',
                    },
                },

                renewal_alert_types: {
                    isValid: () =>
                        !this.contextAccount.isCustom() && this.contextAccount.equity_alert,
                    title: i18n.t('dashboards.alertTypes.columnName'),
                    style: { width: '120px' },
                    component: 'renewal-alert-notification-type',
                    filter: {
                        type: 'select',
                        multiple: true,
                        options: [
                            {
                                value: 'renewalAlertEquity',
                                text: i18n.t('dashboards.alertTypes.equity'),
                            },
                            {
                                value: 'renewalAlertEndLocation',
                                text: i18n.t('dashboards.alertTypes.location'),
                            },
                            {
                                value: 'renewalAlertEndWarranty',
                                text: i18n.t('dashboards.alertTypes.warranty'),
                            },
                        ],
                    },
                },

                repair_order: {
                    title: i18n.t('clientCard.repairOrder'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatRepairOrder(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                result: {
                    title: i18n.t('clientCard.result'),
                    style: { width: '130px' },
                    component: 'result-time',
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'result',
                        options: [
                            'pending',
                            'attempted',
                            'reached',
                            ...(this.contextAccount.isPolestar() ? ['needs_attention'] : []),
                        ],
                    },
                },

                result_wb: {
                    title: i18n.t('general.result', 1),
                    style: { width: '140px' },
                    component: 'result-web-boost',
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'result',
                        options: [
                            'reached',
                            'pending',
                            'calling',
                            'invalid',
                            'attempted',
                            'error',
                            'interrupted',
                            'canceled',
                            'revive',
                            'missed',
                            'unknown',
                        ],
                    },
                },

                road_test: {
                    isValid: () => this.hasClientCardField('performance', 'road_test'),
                    title: i18n.t('result.roadTest'),
                    style: { width: '75px' },
                    component: 'road-test',
                    filter: {
                        multiple: this.dashboardType !== 'service',
                        type: 'select',
                        options: this.roadTestOptions,
                        withNone: this.dashboardType !== 'service',
                        withEmpty: this.dashboardType === 'service',
                        sorting: false,
                    },
                },

                long_term: {
                    isValid: () => this.hasClientCardField('performance', 'long_term'),
                    title: i18n.t('result.longTerm'),
                    style: { width: '85px' },
                    component: 'long-term',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                sale: {
                    isValid: () => this.hasClientCardField('performance', 'sale'),
                    title: i18n.t('result.sale'),
                    style: { width: '75px' },
                    component: 'sale',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                sale_by_phone: {
                    isValid: () =>
                        this.hasClientCardField('performance', 'sale_by_phone') &&
                        this.contextAccount.hasOption('sale_by_phone'),
                    title: `
                        <div class="flex items-center">
                            <span class="mr-2">
                                ${i18n.t('result.sale')}
                            </span>
                            ${getIconMarkup('regular/phone-landline', {
                                class: 'event-icon',
                            })}
                        </div>
                    `,
                    sortTitle: i18n.t('result.sale'),
                    style: { width: '75px' },
                    exportTitle: i18n.t('result.saleByPhone', 1),
                    component: 'sale-by-phone',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                sale_state: {
                    title: i18n.t('clientCard.result'),
                    style: { width: '120px' },
                    component: 'sale-sticker',
                    disableSorting: true,
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'saleTable.filters',
                        options: this.saleStateOptions,
                    },
                },

                search_term: {
                    isValid: () => this.hasClientCardField('general_info', 'search_term'),
                    title: i18n.t('clientCard.searchTerm'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatSearchTerm(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                sector: {
                    isValid: () => this.hasClientCardField('general_info', 'sector'),
                    title: i18n.t('accounts.edit.sector'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatSector(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                segment: {
                    isValid: () =>
                        this.hasClientCardField('general_info', 'segment') ||
                        this.hasHyundaiSupplier,
                    title: i18n.t('clientCard.event.segment'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatEventSegment(lead, this.hasHyundaiSupplier);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'result',
                        options: this.segmentOptions,
                    },
                },

                time_frame: {
                    isValid: () => this.hasClientCardField('general_info', 'time_frame'),
                    title: i18n.t('clientCard.timeFrame'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatTimeFrame(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                service_agents: {
                    title: i18n.t('service.columns.advisorAgent'),
                    style: { width: '165px' },
                    truncating: true,
                    component: 'service-advisor-agent',
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.activeUsers,
                    },
                },

                odometer_last_visit: {
                    title: i18n.t('service.columns.odometerLastVisit'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatOdometerLastVisit(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                service_intention: {
                    title: i18n.t('clientCard.services.intention'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatServiceIntention(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        options: this.intentionsOptions,
                    },
                },

                service_walk_around: {
                    title: i18n.t('clientCard.services.walkAround'),
                    style: { width: '150px' },
                    formater: (lead: any) => {
                        return formater.formatServiceWalkaround(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        options: [
                            {
                                value: 'a1',
                                text: i18n.t('clientCard.services.walkArounds.a1'),
                            },
                            {
                                value: 'scratch',
                                text: i18n.t('clientCard.services.walkArounds.scratch'),
                            },
                            {
                                value: 'brokenGlass',
                                text: i18n.t('clientCard.services.walkArounds.brokenGlass'),
                            },
                            {
                                value: 'bump',
                                text: i18n.t('clientCard.services.walkArounds.bump'),
                            },
                            {
                                value: 'cleaning',
                                text: i18n.t('clientCard.services.walkArounds.cleaning'),
                            },
                            {
                                value: 'other',
                                text: i18n.t('clientCard.services.walkArounds.other'),
                            },
                        ],
                    },
                },

                service_transport: {
                    title: i18n.t('clientCard.services.transport'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatServiceTransport(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        options: [
                            {
                                value: 'courtesy',
                                text: i18n.t('clientCard.services.transports.courtesy'),
                            },
                            {
                                value: 'drop',
                                text: i18n.t('clientCard.services.transports.drop'),
                            },
                            {
                                value: 'limousine',
                                text: i18n.t('clientCard.services.transports.limousine'),
                            },
                            {
                                value: 'other',
                                text: i18n.t('clientCard.services.transports.other'),
                            },
                            {
                                value: 'renting',
                                text: i18n.t('clientCard.services.transports.renting'),
                            },
                            {
                                value: 'wait',
                                text: i18n.t('clientCard.services.transports.wait'),
                            },
                        ],
                    },
                },

                service_interval_km: {
                    title: i18n.t('service.columns.serviceIntervalKm'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatServiceIntervalKm(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        sorting: false,
                        options: [
                            { value: 1000, text: '1000 KM' },
                            { value: 5000, text: '5000 KM' },
                            { value: 6000, text: '6000 KM' },
                            { value: 8000, text: '8000 KM' },
                            { value: 12000, text: '12000 KM' },
                            { value: 16000, text: '16000 KM' },
                            { value: 18000, text: '18000 KM' },
                            { value: 20000, text: '20000 KM' },
                            { value: 24000, text: '24000 KM' },
                            { value: 25000, text: '25000 KM' },
                        ],
                    },
                },

                service_monthly_km: {
                    title: i18n.t('service.columns.serviceMonthlyKm'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatServiceMonthlyKm(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'service_products:air_filter': {
                    isValid: () => this.hasAccountProduct('air_filter'),
                    title: i18n.t('clientCard.products.air_filter'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:alignment': {
                    isValid: () => this.hasAccountProduct('alignment'),
                    title: i18n.t('clientCard.products.alignment'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:body': {
                    isValid: () => this.hasAccountProduct('body'),
                    title: i18n.t('clientCard.products.body'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:brakes': {
                    isValid: () => this.hasAccountProduct('brakes'),
                    title: i18n.t('clientCard.products.brakes'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:diagnostic': {
                    isValid: () => this.hasAccountProduct('diagnostic'),
                    title: i18n.t('clientCard.products.diagnostic'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:injection': {
                    isValid: () => this.hasAccountProduct('injection'),
                    title: i18n.t('clientCard.products.injection'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:maintenance_a': {
                    isValid: () => this.hasAccountProduct('maintenance_a'),
                    title: i18n.t('clientCard.products.maintenance_a'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:maintenance_b': {
                    isValid: () => this.hasAccountProduct('maintenance_b'),
                    title: i18n.t('clientCard.products.maintenance_b'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:maintenance_c': {
                    isValid: () => this.hasAccountProduct('maintenance_c'),
                    title: i18n.t('clientCard.products.maintenance_c'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:maintenance_d': {
                    isValid: () => this.hasAccountProduct('maintenance_d'),
                    title: i18n.t('clientCard.products.maintenance_d'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:maintenance_recommended': {
                    isValid: () => this.hasAccountProduct('maintenance_recommended'),
                    title: i18n.t('clientCard.products.maintenance_recommended'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:oil_filter': {
                    isValid: () => this.hasAccountProduct('oil_filter'),
                    title: i18n.t('clientCard.products.oil_filter'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:others': {
                    isValid: () => this.hasAccountProduct('others'),
                    title: i18n.t('clientCard.products.others'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:parts': {
                    isValid: () => this.hasAccountProduct('parts'),
                    title: i18n.t('clientCard.products.parts'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:pollen_filter': {
                    isValid: () => this.hasAccountProduct('pollen_filter'),
                    title: i18n.t('clientCard.products.pollen_filter'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:tires': {
                    isValid: () => this.hasAccountProduct('tires'),
                    title: i18n.t('clientCard.products.tires'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:transmission': {
                    isValid: () => this.hasAccountProduct('transmission'),
                    title: i18n.t('clientCard.products.transmission'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'service_products:wash': {
                    isValid: () => this.hasAccountProduct('wash'),
                    title: i18n.t('clientCard.products.wash'),
                    style: { width: '120px' },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                source: {
                    isValid: () => this.hasClientCardField('general_info', 'source'),
                    title: 'Source',
                    style: { width: '175px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatSource(lead, true);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.sourceOptions,
                    },
                },

                status: {
                    title: i18n.t('general.status'),
                    style: { width: '75px' },
                    component: 'lead-status',
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'result',
                        withNone: true,
                        sorting: false,
                        noneLabel: i18n.t('general.active'),
                        options: ['sale', 'duplicate', 'invalid', 'lost'],
                    },
                },

                storage: {
                    isValid: () => this.hasClientCardField('general_info', 'storage'),
                    title: i18n.t('service.columns.storage'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatStorage(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                take_over: {
                    isValid: () => this.hasClientCardField('performance', 'take_over'),
                    title: i18n.t('result.takeOver'),
                    style: { width: '75px' },
                    component: 'take-over',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                twenty_four_hour: {
                    isValid: () => this.hasClientCardField('performance', 'twenty_four_hour'),
                    title: i18n.t('result.twenty_four_hour'),
                    style: { width: '75px' },
                    component: 'twenty-four-hour',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'take_over_director:': {
                    isValid: () => this.hasClientCardField('performance', 'take_over'),
                    title: i18n.t('result.takeOverBy'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return lead.takeOverDirectorUser.id
                            ? lead.takeOverDirectorUser.fullName
                            : '-';
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.directorUsers,
                    },
                },

                todo: {
                    title: i18n.t('result.todo'),
                    style: { width: '150px' },
                    component: 'todo',
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.todoOptions,
                    },
                },

                total_minutes: {
                    title: i18n.t('service.columns.totalMinutes'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatTotalMinutes(lead);
                    },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                total_price: {
                    title: i18n.t('service.columns.totalPrice'),
                    style: { width: '120px' },
                    baseClass: 'column-total-price',
                    tooltip: i18n.t('commercial.totalPrice'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatTotalPrice(lead, false, 'service');
                    },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                total_price_commercial_director: {
                    title: i18n.t('service.columns.totalPrice'),
                    style: { width: '120px' },
                    baseClass: 'column-total-price',
                    tooltip: i18n.t('commercial.totalPriceCommercialDirector'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatTotalPrice(lead, false, 'commercial');
                    },
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                unsubscribe: {
                    title: i18n.t('general.optOut'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatUnsubscribe(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'optOut',
                        options: ['email', 'call', 'sms', 'dnd'],
                    },
                },

                updated_at: {
                    title: i18n.t('clientCard.updatedAt'),
                    style: { width: '100px' },
                    formater: (lead: any) => {
                        return asLocale(lead.updated_at, 'updated_at').humanShort() || '-';
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                walk_around: {
                    isValid: () => this.hasClientCardField('performance', 'walk_around'),
                    title: i18n.t('clientCard.walkAround'),
                    style: { width: '75px' },
                    component: 'walk-around',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                waiting_sale: {
                    isValid: () => this.hasClientCardField('process', 'waiting_sale'),
                    title: i18n.t('clientCard.waitingSale', 1),
                    style: { width: '105px' },
                    component: 'waitingSale',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'wanted_vehicles:budget_max': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'budget'),
                    title: i18n.t('clientCard.vehicles.budget_max'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleWantedBudget(lead, 'max');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:budget_min': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'budget'),
                    title: i18n.t('clientCard.vehicles.budget_min'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleWantedBudget(lead, 'min');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:category': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'category'),
                    title: i18n.t('clientCard.vehicles.category'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleCategory(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'clientCard.vehicles',
                        withNone: true,
                        options: [
                            'automotive',
                            'boat',
                            'truck',
                            'van',
                            'hybride',
                            'motorcycle',
                            'watercraft',
                            'snowmobile',
                            'motorized',
                            'caravan',
                            'trailer',
                            'utility',
                            'atv',
                            'suv',
                            'bike',
                            'mechanical',
                        ],
                    },
                },

                'wanted_vehicles:category_rv': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'category_rv'),
                    title: i18n.t('clientCard.vehicles.categoryRV'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleCategoryRV(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'clientCard.vehicles',
                        withNone: true,
                        options: [
                            'class_a_diesel',
                            'hybride',
                            'motorized_a',
                            'motorized_b',
                            'motorized_c',
                            'travel_trailer',
                            'fifth_wheel',
                            'trailer_park',
                            'tent_trailer',
                        ],
                    },
                },

                'wanted_vehicles:color_exterior': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'color_exterior'),
                    title:
                        i18n.t('clientCard.vehicles.color') + String(i18n.t('general.wantedAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleColor(lead, 'wanted', 'color_exterior');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:color_interior': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicles', 'color_interior'),
                    title: `${
                        i18n.t('accounts.edit.colorInterior') + String(i18n.t('general.wantedAbr'))
                    }`,
                    style: { width: '150px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleColor(lead, 'wanted', 'color_interior');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:end_contract_date': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted', 'end_contract_date'),
                    title: `${
                        i18n.t('clientCard.vehicles.contractEnd') +
                        String(i18n.t('general.wantedAbr'))
                    }`,
                    style: { width: '150px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleEndContract(lead, 'wanted');
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:end_warranty_date': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title:
                        i18n.t('clientCard.vehicles.endWarrantyDate') +
                        String(i18n.t('general.wantedAbr')),
                    style: { width: '130px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleEndWarrantyDate(lead, 'wanted');
                    },
                    filterTooltip: i18n.t('dashboards.dateFilterTooltip'),
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:length_max': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'length'),
                    title: `${i18n.t('clientCard.vehicles.length')} max.`,
                    style: { width: '125px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleLength(lead, 'wanted', 'max');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:length_min': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'length'),
                    title: `${i18n.t('clientCard.vehicles.length')} min.`,
                    style: { width: '125px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleLength(lead, 'wanted', 'min');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:make': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('general.make'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleMake(lead, 'wanted');
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'wanted_vehicles:mechanical': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'category'),
                    title: i18n.t('clientCard.vehicles.mechanical'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleMechanical(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        baseTrans: 'clientCard.vehicles',
                        withNone: true,
                        options: [
                            'generator',
                            'outside',
                            'water_pump',
                            'blower',
                            'border_size',
                            'motoculteur',
                            'mower',
                        ],
                    },
                },

                'wanted_vehicles:modality': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'modality'),
                    title: i18n.t('clientCard.vehicles.modality'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleModality(lead, this.dashboardType, 'wanted');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'clientCard.vehicles',
                        options: ['financing', 'leasing', 'cash'],
                    },
                },

                'wanted_vehicles:model': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('general.model'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleModel(lead, 'wanted');
                    },
                    filter: {
                        type: this.accountHasVehiclesModelDropdownOption ? 'select' : 'string',
                        multiple: true,
                        withNone: true,
                        options: () => this.accountVehicleModels.wanted || [],
                        onOpen: () => {
                            if (!this.accountVehicleModelsLoaded) {
                                this.fetchAccountVehicleModels();
                            }
                        },
                    },
                },

                'wanted_vehicles:offer': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'offer'),
                    title: i18n.t('general.offer'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleWantedOffer(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:price': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'price'),
                    title: i18n.t('general.price') + String(i18n.t('general.wantedAbr')),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleWantedPrice(lead);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:profit': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'profit'),
                    title: i18n.t('clientCard.vehicles.profit'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleWantedProfit(lead, this.authUser);
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:rate': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'rate'),
                    title: i18n.t('clientCard.vehicles.rate') + String(i18n.t('general.wantedAbr')),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleRate(lead, 'wanted');
                    },
                    filter: {
                        type: 'text',
                    },
                },

                'wanted_vehicles:recorded_date': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('process', 'recorded'),
                    title: i18n.t('saleTable.recordedDate'),
                    style: { width: '75px' },
                    component: 'recorded',
                    filter: {
                        type: 'select',
                        options: this.saleTableDateOptions,
                        sorting: false,
                        withEmpty: this.dashboardType != 'saleTable',
                        multiple: this.dashboardType == 'saleTable',
                    },
                },

                'wanted_vehicles:stock_state': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'stock'),
                    title: i18n.t('general.stockState'),
                    style: { width: '100px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleWantedStockState(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'saleTable',
                        options: ['stock', 'locate', 'order', 'notAvailable', 'inTransit'],
                    },
                },

                'wanted_vehicles:stock': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'stock'),
                    title: i18n.t('general.stock'),
                    style: { width: '100px' },
                    formater: (lead: any) => {
                        return formater.formatVehicleWantedStockNumber(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'wanted_vehicles:suffix': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'suffix'),
                    title: i18n.t('clientCard.vehicles.suffix'),
                    style: { width: '120px' },
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleWantedSuffix(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'wanted_vehicles:vehicle_state': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'vehicle_state'),
                    title: i18n.t('clientCard.vehicleState') + String(i18n.t('general.wantedAbr')),
                    style: { width: '150px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleState(lead, 'wanted');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.vehicleStateOptions,
                    },
                },

                'wanted_vehicles:transmission': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'transmission'),
                    title: i18n.t('service.categories.transmission'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleTransmission(lead, 'wanted');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        baseTrans: 'clientCard.vehicles',
                        options: ['auto', 'manual', 'sequential'],
                    },
                },

                'wanted_vehicles:verified': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.contextAccount.hasOption('verified_sale'),
                    title: i18n.t('result.verified'),
                    style: { width: '75px' },
                    component: 'verified',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                'wanted_vehicles:version': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('clientCard.vehicles.version'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleTrim(lead, 'wanted');
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'wanted_vehicles:vin': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'vin'),
                    title: i18n.t('clientCard.vehicles.vin'),
                    style: { width: '150px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleVin(lead, 'wanted');
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'wanted_vehicles:weight': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'weight'),
                    title: i18n.t('clientCard.vehicles.weight'),
                    style: { width: '100px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleWeight(lead, 'wanted');
                    },
                    filter: {
                        type: 'string',
                    },
                },

                'wanted_vehicles:fuel': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'fuel'),
                    title: i18n.t('clientCard.vehicles.fuel'),
                    style: { width: '100px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleFuel(lead, 'wanted');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.fuelOptions,
                    },
                },

                'wanted_vehicles:year': {
                    isValid: () => !this.contextAccount.isCustom(),
                    title: i18n.t('general.year'),
                    style: { width: '120px' },
                    tooltip: i18n.t('general.wantedVehicle'),
                    truncating: true,
                    formater: (lead: any) => {
                        return formater.formatVehicleYear(lead, 'wanted');
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        selectAll: false,
                        optionType: 'years',
                    },
                },

                'wanted_vehicles:tire': {
                    isValid: () =>
                        !this.contextAccount.isCustom() &&
                        this.hasClientCardField('wanted_vehicle', 'tire'),
                    title: i18n.t('clientCard.vehicles.tires'),
                    tooltip: i18n.t('clientCard.vehicles.tireTooltip'),
                    style: { width: '100px' },
                    component: 'tire',
                    filter: {
                        type: 'select',
                        withEmpty: true,
                        optionType: 'bool',
                    },
                },

                work_order: {
                    title: i18n.t('clientCard.workOrder'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatWorkOrder(lead);
                    },
                    filter: {
                        type: 'string',
                    },
                },

                updated_by_supplier: {
                    title: i18n.t('allLead.updatedBySupplier'),
                    style: { width: '120px' },
                    formater: (lead: any) => {
                        return formater.formatLeadUpdatedBySupplier(lead);
                    },
                    filter: {
                        type: 'select',
                        multiple: true,
                        withNone: true,
                        options: this.suppliers,
                    },
                },
                ...this.customFieldColumns,
            };
            return mapValues(columns, (column: any, name: any) => {
                return {
                    name,
                    ...column,
                };
            });
        },
        dashboardColumns(): Array<ColumnDefinition> {
            // Actual logic moved to a method so we can test it in isolation
            const cols = computeDashboardColumns(this.dashboardConfigs, this.columns);
            return cols;
        },

        hasClientCardField() {
            return (type: string, field: string): boolean => {
                return get(this.contextAccount.client_card_fields, `${type}.${field}`, true);
            };
        },
        hasAccountProduct() {
            return (product: string): boolean => {
                return (this.contextAccount.products || []).some(
                    (p: any) => p.name == product && p.visible
                );
            };
        },
        accountHasVehiclesModelDropdownOption() {
            return (
                !this.contextAccount.hasVehiclesModelTextOption() && !this.contextAccount.isParent()
            );
        },
        customFieldColumns() {
            const customFields: any = {};

            this.contextAccount.getAllCustomFields().forEach((customField: any) => {
                let filterType = '';
                let filterTooltip: any = '';
                let optionType = '';
                let multiple = false;
                let component: any = false;
                const clientCardSection = customField.section;
                let formaterFunction: any = (lead: any) => {
                    return formater.formatAllCustomField(lead, customField, this.groupDisplay);
                };

                const isVehicleCustomField = ClientCardSection.isVehicleField(customField.section);
                const isActualVehicleCustomField = ClientCardSection.isVehicleExchangeField(
                    customField.section
                );

                switch (customField.type) {
                    case 'array':
                        filterType = 'select';
                        multiple = true;
                        component = 'inline-custom-field-text-edit';
                        break;

                    case 'boolean':
                        filterType = 'select';
                        optionType = 'bool';
                        component = 'custom-checkbox';
                        formaterFunction = false;
                        break;

                    case 'currency':
                        filterType = 'number';
                        component = 'inline-custom-field-text-edit';
                        break;

                    case 'datetime':
                        filterType = 'text';
                        filterTooltip = i18n.t('dashboards.dateFilterWithoutYearTooltip');
                        component = 'inline-custom-field-text-edit';
                        break;

                    case 'textarea':
                        filterType = 'string';
                        component = 'inline-custom-field-text-edit';
                        break;

                    default:
                        filterType = 'string';
                        component = 'inline-custom-field-text-edit';
                        break;
                }

                component = isVehicleCustomField ? 'custom-field-vehicles' : component;

                customFields[`customId_${customField.id}`] = {
                    isValid: () => customField.visible,
                    title: `${customField.name}${
                        isActualVehicleCustomField ? i18n.t('general.currentAbr') : ''
                    }`,
                    tooltip: `${customField.name}${
                        isActualVehicleCustomField ? i18n.t('general.currentAbr') : ''
                    } ${customField.parentName ? ` (${customField.parentName})` : ''}`,
                    style: { width: '120px' },
                    truncating: true,
                    customField,
                    component,
                    clientCardSection,
                    formater: formaterFunction,
                    filterTooltip,
                    filter: {
                        type: filterType,
                        optionType,
                        multiple,
                        withEmpty: !multiple,
                        withNone: multiple,
                        options: customField.select_picker_options
                            .map((option: any) => {
                                return {
                                    value: option,
                                    text: option,
                                };
                            })
                            .sort((a: any, b: any) => a.text.localeCompare(b.text)),
                        sorting: false,
                    },
                };
            });

            return customFields;
        },
    },
    actions: {
        setActiveUsers(users: Array<any>) {
            this.activeUsers = users;
        },

        setActiveUsersAndSupplierUsers(activeUsersAndSupplierUsers: Array<any>) {
            this.activeUsersAndSupplierUsers = activeUsersAndSupplierUsers;
        },

        setCustomerProfileDashboardLayoutEnabled(enabled: boolean) {
            this.customerProfileDashboardLayoutEnabled = enabled;
        },

        setDirectorUsers(directorUsers: Array<any>) {
            this.directorUsers = directorUsers;
        },

        setNotServiceUsers(notServiceUsers: Array<any>) {
            this.notServiceUsers = notServiceUsers;
        },
    },
});
